/**
 * Fail-closed scoping-detection helpers for permission grant entries.
 *
 * Rule: any entry that carries ANY scoping key is considered scoped (never
 * unrestricted), even when the scoping key is one we do not recognise.
 * Unknown scopings are detected by `hasUnrecognizedScoping` so callers can
 * render them safely without guessing their semantics.
 *
 * No React, no app imports — safe to import from any surface.
 */

export type RawScopedEntry = Record<string, unknown>;

/**
 * True when `value` is an entity UUID the backend would accept as a
 * `scoped_target_entity_id`. The backend drops an association entry whose target
 * is not `uuid.UUID(str(value))`-parseable (`resolve_scoping`,
 * permission_scoping.py); this is the frontend mirror of that parser.
 *
 * `uuid.UUID(str(value))` accepts more than the canonical hyphenated spelling:
 * it strips a `urn:uuid:` prefix and surrounding braces and ignores hyphens,
 * then requires exactly 32 hex digits. So compact (`0114…`), braced
 * (`{0114…-…}`), and URN (`urn:uuid:0114…-…`) forms all parse. Mirroring that
 * acceptance set here keeps a legitimately stored non-canonical target from
 * being misclassified as an unrecognized (passthrough-only) scoping, which
 * would drop the association grant out of the editable grant model.
 */
function isParseableEntityUuid(value: string): boolean {
  const hex = value
    .replace(/urn:/gi, '')
    .replace(/uuid:/gi, '')
    .replace(/^[{}]+|[{}]+$/g, '')
    .replace(/-/g, '');
  return /^[0-9a-f]{32}$/i.test(hex);
}

/**
 * True when `key` is a scoping key.
 * Covers both scoping-axis keys (`scoping_*` / `scopingAssociationTypeName`, …) AND
 * scope-target keys (`scoped_target_entity_id` / `scopedTargetEntityId`), in snake_case
 * and camelCase. A target-only entry (e.g. `scoped_target_entity_id` with no
 * `scoping_association_type_name`) is malformed and must still count as scoped so it is
 * never treated as an unrestricted grant — matching the backend's `unrecognized_scoping_keys`,
 * which checks both `scoping_` and `scoped_` prefixes.
 */
function isScopingKey(key: string): boolean {
  return (
    key.startsWith('scoping_') ||
    key.startsWith('scoped_') ||
    /^scoping[A-Z]/.test(key) ||
    /^scoped[A-Z]/.test(key)
  );
}

/**
 * The four known scoping keys, in both snake_case and camelCase — the frontend
 * mirror of the backend's `_KNOWN_SCOPING_KEYS` (permission_scoping.py). Any
 * scoping key outside this set is an unrecognized axis.
 */
const KNOWN_SCOPING_KEYS: ReadonlySet<string> = new Set([
  'scoping_association_type_name',
  'scoped_target_entity_id',
  'scoping_property_type_name',
  'scoping_property_value',
  'scopingAssociationTypeName',
  'scopedTargetEntityId',
  'scopingPropertyTypeName',
  'scopingPropertyValue',
]);

/**
 * True when the entry carries at least one scoping key that is not among the
 * four known keys — detected independently of whether a recognized axis is
 * also present. This is what lets a valid-axis-plus-unknown-key entry be
 * recognized as carrying an unknown scoping (fail-closed passthrough).
 */
function hasUnknownScopingKey(entry: RawScopedEntry): boolean {
  return Object.keys(entry).some(
    key => isScopingKey(key) && !KNOWN_SCOPING_KEYS.has(key)
  );
}

/**
 * True when the entry carries at least one own scoping key, regardless of the
 * key's value. An entry with ANY scoping key is scoped — fail-closed.
 */
export function entryHasAnyScoping(entry: RawScopedEntry): boolean {
  return Object.keys(entry).some(isScopingKey);
}

/**
 * Returns the scoping association type name from the entry, accepting both
 * snake_case and camelCase variants. Returns `undefined` when the value is
 * absent or not a non-empty string.
 */
export function getScopingAssociationTypeName(
  entry: RawScopedEntry
): string | undefined {
  const raw =
    entry['scoping_association_type_name'] ??
    entry['scopingAssociationTypeName'];
  return typeof raw === 'string' && raw.length > 0 ? raw : undefined;
}

/**
 * Returns the association scoping from the entry, accepting both snake_case and
 * camelCase variants. Returns `null` unless the entry resolves to a valid
 * association scoping the backend would ACCEPT — mirroring `resolve_scoping`'s
 * association branch (permission_scoping.py), which drops (never widens) an
 * entry when:
 *  - the association type name is absent, empty, or not a non-empty string;
 *  - the association type name is `_timeline`-suffixed (a reserved
 *    timeline-event identifier, never a real association type); or
 *  - `scoped_target_entity_id` is absent, or not a parseable entity UUID.
 * A valid association scoping requires BOTH a well-formed type name AND a
 * UUID target — the backend drops "type without target" and "target without
 * type" alike. This is why a non-empty type name is not on its own sufficient
 * to treat the association axis as recognized.
 */
export function getAssociationScoping(
  entry: RawScopedEntry
): { associationTypeName: string; targetEntityId: string } | null {
  const associationTypeName = getScopingAssociationTypeName(entry);
  if (associationTypeName === undefined) {
    return null;
  }
  if (associationTypeName.endsWith('_timeline')) {
    return null;
  }
  const rawTarget =
    entry['scoped_target_entity_id'] ?? entry['scopedTargetEntityId'];
  if (typeof rawTarget !== 'string' || !isParseableEntityUuid(rawTarget)) {
    return null;
  }
  return { associationTypeName, targetEntityId: rawTarget };
}

/**
 * Returns the property scoping from the entry, accepting both snake_case and
 * camelCase variants. Returns `null` when EITHER the property type name or the
 * value is absent, empty, or not a string — mirroring the backend's
 * `resolve_scoping`, which drops an entry whose property value is not a non-empty
 * string. A malformed property scoping must never render as a valid one (e.g.
 * "Unowned: ") while the backend silently drops it.
 */
export function getPropertyScoping(
  entry: RawScopedEntry
): { typeName: string; value: string } | null {
  const rawTypeName =
    entry['scoping_property_type_name'] ?? entry['scopingPropertyTypeName'];
  if (typeof rawTypeName !== 'string' || rawTypeName.length === 0) {
    return null;
  }
  const rawValue =
    entry['scoping_property_value'] ?? entry['scopingPropertyValue'];
  if (typeof rawValue !== 'string' || rawValue.length === 0) {
    return null;
  }
  return { typeName: rawTypeName, value: rawValue };
}

/**
 * True when the entry carries any association-axis key (the scoping association
 * type name, or the target id that rides with it), in snake_case or camelCase —
 * regardless of whether the value is valid.
 */
function hasAssociationAxisKey(entry: RawScopedEntry): boolean {
  return (
    'scoping_association_type_name' in entry ||
    'scopingAssociationTypeName' in entry ||
    'scoped_target_entity_id' in entry ||
    'scopedTargetEntityId' in entry
  );
}

/**
 * True when the entry carries any property-axis key (property type name or
 * property value), in snake_case or camelCase — regardless of whether the
 * value is valid.
 */
function hasPropertyAxisKey(entry: RawScopedEntry): boolean {
  return (
    'scoping_property_type_name' in entry ||
    'scopingPropertyTypeName' in entry ||
    'scoping_property_value' in entry ||
    'scopingPropertyValue' in entry
  );
}

/**
 * True when the entry carries a scoping the GrantSelection model cannot
 * represent, so the caller must preserve it verbatim rather than rebuild it.
 * Cases, checked independently:
 *  - it has an unknown `scoping_*`/`scoped_*` key (even alongside a valid axis) —
 *    otherwise the edit flow rebuilds from the recognized axis and silently
 *    discards the unknown key;
 *  - it has property-axis keys that do not resolve to a valid property scoping
 *    (even alongside a valid association axis) — the backend fail-closed drops
 *    the whole entry, so rebuilding from the association alone would silently
 *    discard the malformed property axis; or
 *  - it has association-axis keys that do not resolve to a valid association
 *    scoping (even alongside a valid property axis) — a target-only axis, a
 *    non-empty type name with a missing/invalid-UUID target, or a
 *    `_timeline`-suffixed type name. The backend fail-closed drops each of
 *    these, so rebuilding from the recognized axis alone (or reconstructing a
 *    target from the public/private toggle on save) would silently discard the
 *    malformed axis the backend never accepted.
 * Every entry any of these matches is still scoped — never treat it as an
 * unrestricted grant.
 */
export function hasUnrecognizedScoping(entry: RawScopedEntry): boolean {
  if (hasUnknownScopingKey(entry)) {
    return true;
  }
  if (hasPropertyAxisKey(entry) && getPropertyScoping(entry) === null) {
    return true;
  }
  return hasAssociationAxisKey(entry) && getAssociationScoping(entry) === null;
}

/**
 * True when `entry` is a valid grant entry: a non-empty-string
 * `entity_type_name`. This is a prerequisite for the downstream grouping
 * logic that organizes permissions by entity type — an entry that fails this
 * check has nothing to group by and must be dropped.
 */
function isPermissionGrant(
  entry: unknown
): entry is RawScopedEntry & { entity_type_name: string } {
  if (typeof entry !== 'object' || entry === null) {
    return false;
  }
  const name = (entry as { entity_type_name?: unknown }).entity_type_name;
  return typeof name === 'string' && name.length > 0;
}

/**
 * Parses a stored permission-grant list into its valid entries. Self-parsing:
 * `value` may already be an array or still the raw JSON string form, matching
 * how the property value arrives from different callers. Returns `[]` for a
 * malformed JSON string or a parsed value that isn't an array, and filters
 * the result via `isPermissionGrant` so every returned entry is guaranteed a
 * non-empty-string `entity_type_name`.
 */
export function parsePermissionGrantEntries(value: unknown): RawScopedEntry[] {
  let parsed: unknown = value;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch {
      return [];
    }
  }
  return Array.isArray(parsed) ? parsed.filter(isPermissionGrant) : [];
}
